<script lang="ts">
  import { Component, Vue, Prop } from 'vue-property-decorator';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import SimpleScrollableModal from '../base/SimpleScrollableModal.vue';
  import ItemsService from '@/api/services/items.service';
  import { IItem } from '@/interfaces/IItem';
  import CategoriesService from '@/api/services/categories.service';
  import RestaurantsService from '@/api/services/restaurants.service';
  import { ICategory } from '@/interfaces/ICategory';
  import StickerSetLabelModal from './StickerSetLabelModal.vue';
  import StickerViewLabelModal from './StickerViewLabelModal.vue';
  import { useMenuStore } from '@/composition/use-menu';

  @Component({
    components: { SimpleScrollableModal },
  })
  export default class StickerProductsModal extends Vue {
    @Prop() restaurant: IRestaurant;
    items: IItem[] | null = null;
    categories: ICategory[] = null;
    selectedItems: IItem[] = [];
    madeChanges = false;
    menuStore = useMenuStore();

    async created() {}

    async mounted() {
      await this.reloadData();
    }

    async reloadData() {
      await this.loadCategories();
      await this.loadItems();
    }

    async loadItems() {
      try {
        const items = (await ItemsService.getAll(this.restaurant.id)).filter(
          (i) => i.visible
        );
        this.selectedItems = [];

        for (const item of items) {
          item.category = this.categoriesById[item.categoryId];
        }

        // sort by category index and item index
        items.sort((a, b) => {
          if (
            !a.category ||
            !b.category ||
            a.category.index === b.category.index
          )
            return a.index - b.index;
          return a.category.index - b.category.index;
        });

        this.items = items;
      } catch (e) {}
    }

    async loadCategories() {
      try {
        const categories = await RestaurantsService.getCategories(
          this.restaurant.id
        );

        // order by index
        categories.sort((a, b) => a.index - b.index);

        this.categories = categories;
      } catch (e) {}
    }

    get itemsWithLabel() {
      return this.items.filter((item) => item.labelTemplate);
    }

    get itemsWithoutLabel() {
      return this.items.filter((item) => !item.labelTemplate);
    }

    get categoriesById() {
      const categoriesById = {};

      for (const category of this.categories) {
        categoriesById[category.id] = category;
      }

      return categoriesById;
    }

    headers = [
      { text: 'Categoría', value: 'category.name', width: 200 },
      { text: 'Nombre', value: 'name' },
    ];

    get headersWithTemplate() {
      return [
        ...this.headers,
        { text: 'Etiqueta', value: 'labelTemplate', width: 100 },
      ];
    }

    setLabels(baseTemplate?: string) {
      this.$modal.show(
        StickerSetLabelModal,
        {
          allItems: this.items,
          baseTemplate,
        },
        {
          name: 'sticker-set-label',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 680,
          clickToClose: false,
        },
        {
          submit: async (labelTemplate: string) => {
            try {
              this.madeChanges = true;
              console.log(labelTemplate);
              await ItemsService.massUpdateLabelTemplate(
                this.restaurant.id,
                this.selectedItems.map((i) => i.id),
                labelTemplate
              );

              // Update the menu store: its items are replaced, never edited.
              const updated = this.selectedItems.flatMap((item) => {
                const stored = this.menuStore.itemMap?.[item.id];
                return stored ? [{ ...stored, labelTemplate }] : [];
              });
              this.menuStore.upsertItems(updated, { own: true });

              await this.reloadData();
              this.$modal.hide('sticker-set-label');
            } catch (e) {
              console.error(e);
              this.$modal.hide('sticker-set-label');
              this.setLabels(labelTemplate);
            }
          },
        }
      );
    }

    viewLabel(item: IItem) {
      this.$modal.show(
        StickerViewLabelModal,
        {
          item,
        },
        {
          name: 'sticker-view-label',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 680,
          clickToClose: false,
        }
      );
    }

    close() {
      if (!this.madeChanges) this.$emit('close');
      else this.$parent.$emit('submit');
    }
  }
</script>

<template>
  <simple-scrollable-modal>
    <template #title>
      <h3>Etiquetas de productos</h3>
    </template>
    <template #content>
      <template v-if="!items">
        <v-progress-linear indeterminate color="primary"></v-progress-linear>
      </template>
      <template v-else>
        <div class="sticker-products">
          <div class="items-actions">
            <span class="count">{{ selectedItems.length }} seleccionados</span>
            <v-btn
              color="primary"
              :disabled="!selectedItems.length"
              @click="setLabels()"
              small
              >Establecer etiqueta</v-btn
            >
          </div>
          <template v-if="itemsWithLabel.length">
            <div class="group">
              <h4 class="ml-1">Productos con etiqueta</h4>
              <v-data-table
                v-model="selectedItems"
                :headers="headersWithTemplate"
                :items="itemsWithLabel"
                dense
                show-select
                :mobile-breakpoint="0"
                calculate-widths
                hide-default-footer
                :items-per-page="-1"
                class="items-table elevation-0 v-data-table--striped v-data-table--orange mt-1"
              >
                <template #item.menuId="{ item }"
                  ><span class="grey--text">{{
                    item.menu.name
                  }}</span></template
                >
                <template #item.category.name="{ item }"
                  ><span class="grey--text">{{
                    item.category ? item.category.name : ''
                  }}</span></template
                >
                <template #item.labelTemplate="{ item }">
                  <v-btn text small color="info" @click="viewLabel(item)"
                    >Ver</v-btn
                  >
                </template>
              </v-data-table>
            </div>
          </template>
          <template v-if="itemsWithoutLabel.length">
            <div class="group">
              <h4 class="ml-1">Productos sin etiqueta</h4>
              <v-data-table
                v-model="selectedItems"
                :headers="headers"
                :items="itemsWithoutLabel"
                dense
                show-select
                calculate-widths
                :mobile-breakpoint="0"
                hide-default-footer
                :items-per-page="-1"
                class="items-table elevation-0 v-data-table--striped v-data-table--orange mt-1"
              >
                <template #item.menuId="{ item }"
                  ><span class="grey--text">{{
                    item.menu.name
                  }}</span></template
                >
                <template #item.category.name="{ item }"
                  ><span class="grey--text">{{
                    item.category ? item.category.name : ''
                  }}</span></template
                >
              </v-data-table>
            </div>
          </template>
        </div>
      </template>
    </template>
    <template #action-bar>
      <div class="d-flex flex-row justify-end">
        <v-btn color="info" depressed @click="close"> Cerrar </v-btn>
      </div>
    </template>
  </simple-scrollable-modal>
</template>

<style scoped lang="scss">
  .sticker-products {
    @apply tw-space-y-4;
  }
  .items-actions {
    @apply tw-flex tw-items-center tw-justify-end tw-space-x-4;

    .count {
      @apply tw-text-sm;
      color: #999;
    }
  }
  .group {
    h4 {
      @apply tw-font-medium;
    }

    .items {
      .item {
        .image {
        }

        .name {
        }
      }
    }
  }
</style>
